import { Button, Callout, PageModal } from '@dev-spendesk/grapes';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { AnalyticalFieldSelector } from 'src/core/modules/accounting-integration/components/AnalyticalFieldMapping/AnalyticalFieldSelector';
import { FieldValuesMapping } from 'src/core/modules/accounting-integration/components/AnalyticalFieldMapping/FieldValuesMapping/FieldValuesMapping';
import {
  type AnalyticalFieldMappingFieldType,
  type AnalyticalFieldMappingV2,
  type InternalValue,
} from 'src/core/modules/bookkeep';

import { SyncSettingsDropdown } from './SyncSettings/SyncSettingsDropdown';
import { useAccountingSoftwareName } from '../../apis';
import { type AnalyticalFieldExportSettings } from '../../apis/useAnalyticalFieldExportSettings';
import { type SyncMode } from '../../apis/useSetSyncSettings';
import { type SaveAllChanges } from '../../hooks/useAnalyticalFieldMapping';
import { useAnalyticalFieldMappingMaintenanceModal } from '../../hooks/useAnalyticalFieldMappingMaintenanceModal';
import { ExportSettings } from '../ExportSettings/ExportSettings';

type Props<TField extends InternalValue, TValue extends InternalValue> = {
  internalField: TField;
  fieldType: AnalyticalFieldMappingFieldType;
  isOpen: boolean;
  exportConfig: AnalyticalFieldExportSettings[];
  isIntegrationNameLoading: boolean;
  isExportConfigEnabled: boolean;
  isSplittable: boolean;
  analyticalFieldMappings: AnalyticalFieldMappingV2[];
  values: TValue[];
  syncMode: SyncMode | undefined;
  onClose: () => void;
  onSaveChanges: SaveAllChanges;
};

export const AnalyticalFieldMappingMaintenanceModal = <
  TField extends InternalValue,
  TValue extends InternalValue,
>({
  isOpen,
  fieldType,
  analyticalFieldMappings,
  exportConfig,
  internalField,
  syncMode,
  isIntegrationNameLoading,
  isExportConfigEnabled,
  isSplittable,
  values,
  onClose,
  onSaveChanges,
}: Props<TField, TValue>) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName, integration } = useAccountingSoftwareName();
  const {
    title,
    mode,
    fieldMapping,
    selectedExportSettings,
    selectedSyncMode,
    mappingInDatabase,
    mappingInDatabaseCalloutWarningPoints,
    nextButton,
    backButton,
    errors,
    fieldMappingValues,
    setFieldMappingValues,
    handleSyncModeChanged,
    handleFieldMappingSelected,
    handleUpdateSettings,
    handleExportSettingsChanged,
    setFieldMapping,
    handleBack,
    handleClose,
  } = useAnalyticalFieldMappingMaintenanceModal({
    fieldType,
    analyticalFieldMappings,
    exportConfig,
    internalField,
    syncMode,
    isExportConfigEnabled,
    onClose,
    onSaveChanges,
    internalValues: values,
  });

  if (!selectedSyncMode) {
    return null;
  }

  return (
    <PageModal isOpen={isOpen} onClose={handleClose} title={<></>}>
      <div className="m-auto h-full w-full max-w-md pt-32">
        <h1 className="heading-l mb-32 text-center">{title}</h1>
        {mode === 'settings' && (
          <>
            <div className="mb-8">
              <SyncSettingsDropdown
                fieldName={internalField.name}
                value={selectedSyncMode}
                onChange={handleSyncModeChanged}
                allowedSyncSettings={
                  internalField.id === 'costCenter'
                    ? ['pullAndMap', 'none']
                    : undefined
                }
              />
            </div>

            {syncMode === 'pullAndMap' && selectedSyncMode === 'pull' && (
              <Callout
                variant="info"
                title={
                  <ul className="ml-24 list-disc">
                    <li>
                      {t(
                        'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.changeFromMapToPull.newSourceOfTruth',
                        { integration: accountingSoftwareName },
                      )}
                    </li>
                    <li>
                      {t(
                        'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.changeFromMapToPull.addUnmappedValues',
                        { integration: accountingSoftwareName },
                      )}
                    </li>
                  </ul>
                }
              />
            )}

            {syncMode === 'pull' && selectedSyncMode === 'pullAndMap' && (
              <Callout
                variant="info"
                title={t(
                  'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.changeFromPullToMap',
                  { integration: accountingSoftwareName },
                )}
              />
            )}

            {selectedSyncMode === 'none' && fieldMapping?.internalId && (
              <Callout
                variant="warning"
                title={t(
                  'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.changeToNoSyncWarning',
                  {
                    fieldName: fieldMapping?.externalName,
                    integration: accountingSoftwareName,
                  },
                )}
              />
            )}

            {selectedSyncMode !== 'none' && (
              <>
                <div className="mt-32 mb-40">
                  <AnalyticalFieldSelector
                    syncMode={selectedSyncMode}
                    fieldMapping={fieldMapping}
                    fieldName={internalField.name}
                    fields={analyticalFieldMappings}
                    hasError={errors.selectedField}
                    onValueSelected={handleFieldMappingSelected}
                  />
                  {syncMode !== 'none' &&
                    mappingInDatabase?.id !== fieldMapping?.id && (
                      <div className="mt-8">
                        <Callout
                          variant="warning"
                          title={
                            <ul className="ml-24 list-disc">
                              {mappingInDatabaseCalloutWarningPoints.map(
                                (message, index) => (
                                  <li
                                    key={`previous_mapping_callout_point_${index + 1}`}
                                  >
                                    {message}
                                  </li>
                                ),
                              )}
                            </ul>
                          }
                        />
                      </div>
                    )}
                </div>
                {integration === 'Netsuite' && (
                  <div className="mb-40">
                    <ExportSettings
                      fieldType={fieldType}
                      syncMode={selectedSyncMode}
                      hasError={errors.exportSettings}
                      isSplittable={isSplittable}
                      externalField={
                        fieldMapping
                          ? {
                              id: fieldMapping.externalId,
                              name: fieldMapping.externalName,
                            }
                          : undefined
                      }
                      value={selectedExportSettings}
                      fieldName={internalField.name}
                      variant="secondary"
                      onChange={handleExportSettingsChanged}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
        {mode === 'remap' && (
          <>
            <FieldValuesMapping
              fieldType={fieldType}
              isIntegrationNameLoading={isIntegrationNameLoading}
              internalField={internalField}
              syncMode={selectedSyncMode}
              analyticalFieldMappings={analyticalFieldMappings}
              internalValues={values}
              fieldMapping={fieldMapping}
              setFieldMapping={setFieldMapping}
              fieldMappingValues={fieldMappingValues}
              setFieldMappingValues={setFieldMappingValues}
              hideFieldSelector
              hideBorder
            />
          </>
        )}
      </div>
      <div className="border-border-default bg-background-primary-default absolute inset-x-0 bottom-0 w-full border-t-1 py-16">
        <div className="m-auto w-full max-w-md">
          <div className="flex justify-between">
            <div>
              <Button
                variant="secondaryNeutral"
                text={backButton.text}
                iconName={backButton.iconName}
                iconPosition="left"
                onClick={handleBack}
              />
            </div>
            <div>
              <Button
                variant="primaryBrand"
                text={nextButton.text}
                iconName={nextButton.iconName}
                iconPosition="right"
                onClick={handleUpdateSettings}
              />
            </div>
          </div>
        </div>
      </div>
    </PageModal>
  );
};
